import { Suspense, useState, useRef, useImperativeHandle, forwardRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import type { Flight, Airport } from '@/types';
import { Earth } from './Earth';
import { Starfield } from './Starfield';
import { FlightPath } from './FlightPath';
import { AirportMarker } from './AirportMarker';
import { AirportVideoModal } from './AirportVideoModal';
import { hasAirportVideo } from '@/data/airportVideos';

// Color palette for flight paths
const COLORS = [
  '#00d4ff', // Cyan
  '#ff6b9d', // Pink
  '#7b2cbf', // Purple
  '#00ff88', // Green
  '#ffcc00', // Yellow
  '#ff4444', // Red
];

interface GlobeProps {
  flights: Flight[];
  autoRotate?: boolean;
}

export interface GlobeHandle {
  resetView: () => void;
}

export const Globe = forwardRef<GlobeHandle, GlobeProps>(({ flights, autoRotate = true }, ref) => {
  const [selectedAirport, setSelectedAirport] = useState<Airport | null>(null);
  const [videoAirport, setVideoAirport] = useState<Airport | null>(null);
  const controlsRef = useRef<OrbitControlsImpl>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera>(null);

  // Handle airport click - show video if available, otherwise show info popup
  const handleAirportClick = (airport: Airport) => {
    if (hasAirportVideo(airport.iata)) {
      setVideoAirport(airport);
    } else {
      setSelectedAirport(airport);
    }
  };

  useImperativeHandle(ref, () => ({
    resetView: () => {
      if (controlsRef.current && cameraRef.current) {
        // Reset camera position
        cameraRef.current.position.set(0, 0, 3);
        cameraRef.current.lookAt(0, 0, 0);
        
        // Reset controls target
        controlsRef.current.target.set(0, 0, 0);
        controlsRef.current.update();
      }
    }
  }));

  // Get unique airports from flights
  const uniqueAirports = new Map<string, { airport: (typeof flights)[0]['fromAirport']; color: string }>();
  
  flights.forEach((flight, index) => {
    const color = COLORS[index % COLORS.length];
    if (!uniqueAirports.has(flight.fromAirport.iata)) {
      uniqueAirports.set(flight.fromAirport.iata, { airport: flight.fromAirport, color });
    }
    if (!uniqueAirports.has(flight.toAirport.iata)) {
      uniqueAirports.set(flight.toAirport.iata, { airport: flight.toAirport, color });
    }
  });

  return (
    <div className="w-full h-full">
      <Canvas>
        <PerspectiveCamera ref={cameraRef} makeDefault position={[0, 0, 3]} />
        
        <OrbitControls
          ref={controlsRef}
          enableDamping
          dampingFactor={0.05}
          minDistance={1.5}
          maxDistance={10}
          enablePan={false}
          autoRotate={autoRotate}
          autoRotateSpeed={0.5}
          rotateSpeed={0.3}
          zoomSpeed={0.4}
          touches={{
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_PAN
          }}
          mouseButtons={{
            LEFT: THREE.MOUSE.ROTATE,
            MIDDLE: THREE.MOUSE.DOLLY,
            RIGHT: THREE.MOUSE.PAN
          }}
        />

        {/* Lighting */}
        <ambientLight intensity={1.0} />
        <directionalLight position={[5, 3, 5]} intensity={1.5} />
        <directionalLight position={[-5, 0, -5]} intensity={0.3} color={0x4fc3f7} />

        <Suspense fallback={null}>
          {/* Background stars */}
          <Starfield />

          {/* Earth with clouds and atmosphere */}
          <Earth autoRotate={false} />

          {/* Flight paths with animated planes */}
          {flights.map((flight, index) => (
            <FlightPath
              key={flight.id}
              from={flight.fromAirport}
              to={flight.toAirport}
              color={COLORS[index % COLORS.length]}
              showPlane
            />
          ))}

          {/* Airport markers */}
          {Array.from(uniqueAirports.values()).map(({ airport, color }) => (
            <AirportMarker
              key={airport.iata}
              airport={airport}
              color={color}
              hasVideo={hasAirportVideo(airport.iata)}
              onClick={() => handleAirportClick(airport)}
            />
          ))}
        </Suspense>
      </Canvas>

      {/* Airport details popup */}
      {selectedAirport && (
        <div className="absolute top-16 md:top-20 left-1/2 transform -translate-x-1/2 z-50 bg-slate-800 border border-cyan-500/30 rounded-lg shadow-2xl p-4 md:p-6 min-w-[280px] md:min-w-[320px] max-w-[90vw]">
          <button
            onClick={() => setSelectedAirport(null)}
            className="absolute top-2 right-2 text-gray-400 hover:text-white transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white">{selectedAirport.iata}</h3>
              </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-start">
                <span className="text-gray-400 font-medium">Airport:</span>
                <span className="text-white text-right max-w-[200px]">{selectedAirport.name}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-400 font-medium">City:</span>
                <span className="text-white">{selectedAirport.city}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-gray-400 font-medium">Country:</span>
                <span className="text-white">{selectedAirport.country}</span>
              </div>
              
              <div className="border-t border-gray-700 pt-2 mt-3">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400 font-medium">Coordinates:</span>
                  <span className="text-gray-300 font-mono">
                    {selectedAirport.lat.toFixed(4)}°, {selectedAirport.lon.toFixed(4)}°
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Airport flyover video modal */}
      {videoAirport && (
        <AirportVideoModal
          airport={videoAirport}
          onClose={() => setVideoAirport(null)}
          onVideoEnd={() => setVideoAirport(null)}
        />
      )}
    </div>
  );
});
